import {
  CREATE_GROUP,
  GET_SINGLE_USER_GROUPS,
  UPDATE_GROUP,
  GET_GROUP_DETAILS,
  GET_SINGLE_GROUP_MEMBERS,
  ADD_GROUP_MEMBER,
  DELETE_SINGLE_GROUP,
  REMOVE_MEMBER_FROM_GROUP,
} from "../end-points/group";
import {
  getFetcher,
  postFetcher,
  formDataPostFetcher,
  formDataPatchFetcher,
  deleteFetcher,
} from "../fetchers";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Toast } from "@/component/Shared/Toast";

const useCreateGroup = () => {
  const queryClient = useQueryClient();

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [CREATE_GROUP],
      mutationFn: formDataPostFetcher,
      onSuccess: (data) => {
        Toast.success(data?.message);

        queryClient.invalidateQueries({ queryKey: [GET_SINGLE_USER_GROUPS] });
      },
      onError: (error) => {
        Toast.error(error?.message);
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error,
    data,
    reset,
    trigger: (args: any) => mutate({ url: `${CREATE_GROUP}`, args: args }),
  };
};

const useUpdateGroup = (groupId: string) => {
  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [UPDATE_GROUP],
      mutationFn: formDataPatchFetcher,
      onSuccess: (data) => {
        Toast.success(data?.message);
      },
      onError: (error) => {
        Toast.error(error?.message);
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error,
    data,
    reset,
    trigger: (args: any) =>
      mutate({
        url: `${UPDATE_GROUP}/${groupId}`,
        args: args,
      }),
  };
};

const useGetGroupDetails = (groupId: string) => {
  const { isPending, isFetching, error, data, isError, isSuccess, refetch } =
    useQuery({
      queryKey: [GET_GROUP_DETAILS],
      queryFn: () => getFetcher(`${GET_GROUP_DETAILS}/${groupId}`),
      //if set to true, API will be called the components in which it is used will be rendered
      refetchOnMount: false,
      // switching between chrome tabs
      refetchOnWindowFocus: false,
      enabled: true, // Set this to false to disable this query from automatically running.
      retry: 0, // Ensures the API is only retried once on failure
    });

  return {
    data: data?.data,
    isLoading: isPending || isFetching,
    error,
    isError,
    isSuccess,
    refetch,
  };
};

const useGetUserGroups = () => {
  const { isPending, isFetching, error, data, isError, isSuccess, refetch } =
    useQuery({
      queryKey: [GET_SINGLE_USER_GROUPS],
      queryFn: () => getFetcher(GET_SINGLE_USER_GROUPS),
      //if set to true, API will be called the components in which it is used will be rendered
      refetchOnMount: false,
      // switching between chrome tabs
      refetchOnWindowFocus: false,
      enabled: true, // Set this to false to disable this query from automatically running.
      retry: 0, // Ensures the API is only retried once on failure
    });

  return {
    data: data?.data,
    isLoading: isPending || isFetching,
    error,
    isError,
    isSuccess,
    refetch,
  };
};

const useAddGroupMember = () => {
  const queryClient = useQueryClient();

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [ADD_GROUP_MEMBER],
      mutationFn: postFetcher,
      onSuccess: (data) => {
        Toast.success(data?.message);
        //
        queryClient.setQueryData([GET_GROUP_DETAILS], (oldData: any) => ({
          ...oldData,
          data: data?.data,
        }));

        // queryClient.setQueryData([GET_PROFILE_STATUS], (oldData: any) => ({
        //   ...oldData,
        //   user: {
        //     ...oldData?.user,
        //     userInformation: true,
        //   },
        // }));
        //
      },
      onError: (error) => {
        Toast.error(error?.message);
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error,
    data,
    reset,
    trigger: (args: any) =>
      mutate({
        url: `${ADD_GROUP_MEMBER}`,
        args: args,
      }),
  };
};

const useGetGroupMembers = (groupId: string) => {
  const { isPending, isFetching, error, data, isError, isSuccess, refetch } =
    useQuery({
      queryKey: [GET_SINGLE_GROUP_MEMBERS],
      queryFn: () => getFetcher(`${GET_SINGLE_GROUP_MEMBERS}/${groupId}`),
      //if set to true, API will be called the components in which it is used will be rendered
      refetchOnMount: true,
      // switching between chrome tabs
      refetchOnWindowFocus: false,
      enabled: true, // Set this to false to disable this query from automatically running.
      retry: 0, // Ensures the API is only retried once on failure
    });

  return {
    data: data?.data,
    isLoading: isPending || isFetching,
    error,
    isError,
    isSuccess,
    refetch,
  };
};

const useDeleteSingleGroup = () => {
  const queryClient = useQueryClient();

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [DELETE_SINGLE_GROUP],
      mutationFn: (payloadData: any) =>
        deleteFetcher(`${DELETE_SINGLE_GROUP}/${payloadData?.groupId}`),
      onSuccess: (data) => {
        Toast.success(data?.message);

        // Re-call these APIs to fetch latest data
        queryClient.invalidateQueries({ queryKey: [GET_SINGLE_USER_GROUPS] });
        queryClient.invalidateQueries({ queryKey: [GET_SINGLE_GROUP_MEMBERS] });
      },
      onError: (error: any) => {
        Toast.error(error?.message);
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error: error,
    data: data,
    reset: reset,
    trigger: (groupId: string) => mutate({ groupId }),
  };
};

const useRemoveMemberFromGroup = () => {
  const queryClient = useQueryClient();

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [REMOVE_MEMBER_FROM_GROUP],
      mutationFn: (payloadData: any) =>
        deleteFetcher(
          `${REMOVE_MEMBER_FROM_GROUP}/${payloadData?.groupId}/${payloadData?.groupMemberId}`
        ),
      onSuccess: (data) => {
        Toast.success(data?.message);

        // Re-call these APIs to fetch latest data
        queryClient.invalidateQueries({ queryKey: [GET_SINGLE_USER_GROUPS] });
        queryClient.invalidateQueries({ queryKey: [GET_SINGLE_GROUP_MEMBERS] });
      },
      onError: (error: any) => {
        Toast.error(error?.message);
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error: error,
    data: data,
    reset: reset,
    trigger: (groupId: string, groupMemberId: string) =>
      mutate({ groupId, groupMemberId }),
  };
};

export {
  useCreateGroup,
  useUpdateGroup,
  useGetUserGroups,
  useGetGroupDetails,
  useGetGroupMembers,
  useAddGroupMember,
  useDeleteSingleGroup,
  useRemoveMemberFromGroup,
};
